// Libs
import React, {
  useEffect,
  useRef,
  PropsWithChildren,
  Children,
  isValidElement,
  Dispatch,
  useState,
  MouseEvent,
  SetStateAction,
} from 'react';
import { throttle } from 'lodash';

// Components
import { Icon } from '@components/Icon';

// Types
import { TabInfo } from '@components/TableWithTabs/types';

interface Props {
  setActiveTab?: Dispatch<SetStateAction<TabInfo>>;
  scrollToTab?: number;
}

const HorizontalScrollMobile = ({
  children,
  setActiveTab,
  scrollToTab,
}: PropsWithChildren<Props>) => {
  const [slideLeft, setSlideLeft] = useState(0);
  const summaryRef = useRef<HTMLDivElement | null>(null);
  enum summaryScroll {
    Left = -150,
    Right = +150,
  }

  useEffect(() => {
    if (scrollToTab && summaryRef.current) {
      summaryRef.current.scrollLeft += scrollToTab;
    }
  }, [scrollToTab]);

  useEffect(() => {
    const handleSummaryScroll = throttle((e: Event) => {
      const summary = e.target;

      summary && setSlideLeft((summary as Element).scrollLeft);
    }, 200);

    summaryRef.current?.addEventListener('scroll', handleSummaryScroll);

    return () => {
      summaryRef.current?.removeEventListener('scroll', handleSummaryScroll);
    };
  }, []);

  const handleSummaryScroll = (scrollOffset: summaryScroll) => {
    if (summaryRef.current) {
      const amountToAdd =
        scrollOffset === summaryScroll.Left
          ? summaryScroll.Left
          : summaryScroll.Right;
      setSlideLeft((summaryRef.current.scrollLeft += amountToAdd));
    }
  };

  return (
    <>
      <div
        className="overflow-x-scroll desktop:overflow-x-auto flex hide-scrollbar desktop:show-scrollbar"
        ref={summaryRef}
        data-testid="overflowContainer"
      >
        {Children.map(children, child =>
          isValidElement(child) ? (
            <child.type
              {...child.props}
              onClick={(e: MouseEvent) => {
                summaryRef.current &&
                  setActiveTab?.({
                    name: child.props['data-active-tab'],
                    scrollTo: summaryRef.current.scrollLeft +=
                      (e.target as Element).getBoundingClientRect().left -
                      summaryRef.current.getBoundingClientRect().left,
                  });
              }}
            ></child.type>
          ) : (
            child
          )
        )}
      </div>
      {slideLeft > 0 && (
        <div
          aria-hidden="true"
          className="tablet:hidden absolute left-8 absolute-vertical-center cursor-pointer"
          onClick={() => handleSummaryScroll(summaryScroll.Left)}
          data-testid="leftSlideButton"
        >
          <Icon name="arrows/left" size="17" color="black" />
        </div>
      )}
      {(!slideLeft || slideLeft < Number(summaryRef.current?.clientWidth)) && (
        <div
          aria-hidden="true"
          className="tablet:hidden absolute right-8 absolute-vertical-center cursor-pointer"
          onClick={() => handleSummaryScroll(summaryScroll.Right)}
          data-testid="rightSlideButton"
        >
          <Icon name="arrows/right" size="17" color="black" />
        </div>
      )}
    </>
  );
};

export default HorizontalScrollMobile;
